/**
 * HW Product Story — turns a plain product description into the structured
 * layout used on the 10 Bar MAP sensor page (VERSION 1.6 - Design Refresh).
 *
 * It only RE-ARRANGES what the merchant already wrote in the Shopify product
 * description — no wording is added, changed or removed:
 *
 *   first paragraph ................ lead under the section title
 *   headings / bold-only lines ..... section titles
 *   "Name — description" runs ...... card grid
 *   bullet / numbered lists ........ check lists
 *   "Note:" / "Important:" ......... callouts
 *   link-only lines (PDFs etc.) .... resource buttons (attached to the card above)
 *   videos, images, tables ......... framed media / styled table
 *
 * If anything unexpected happens the original description is left exactly as
 * it was (styled as readable prose by hw-product-story.css).
 */
(function () {
  'use strict';

  var ICON = {
    note: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
    doc: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>',
    ext: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>',
    mail: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>'
  };

  var LEAF = { P: 1, H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1, UL: 1, OL: 1, DL: 1, TABLE: 1, BLOCKQUOTE: 1, PRE: 1, HR: 1, IFRAME: 1, VIDEO: 1, FIGURE: 1 };
  var CONTAINER = { DIV: 1, SECTION: 1, ARTICLE: 1, MAIN: 1, CENTER: 1, HEADER: 1, FOOTER: 1, ASIDE: 1 };
  var BLOCKISH = 'p,h1,h2,h3,h4,h5,h6,ul,ol,dl,table,blockquote,pre,hr,iframe,video,figure,div,section';
  var NOTE_RE = /^\s*(note|notes|important|warning|caution|please note|disclaimer|attention|heads up)\b\s*[:\-\u2013\u2014!]/i;

  function txt(node) { return node ? (node.textContent || '').replace(/[\u00a0\s]+/g, ' ').trim() : ''; }
  function mk(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function stripEnd(s) { return s.replace(/\s*[:\u2014\u2013\-]+\s*$/, '').trim(); }

  /* ── 1. tidy pasted markup ───────────────────────────────────────────────── */
  function clean(body) {
    Array.prototype.forEach.call(body.querySelectorAll('svg'), function (svg) {
      // pasted icon sprites reference symbols that do not exist on this site
      if (svg.querySelector('use')) svg.parentNode.removeChild(svg);
    });
    Array.prototype.forEach.call(body.querySelectorAll('*'), function (node) {
      var cls = node.getAttribute('class');
      if (cls && cls.indexOf('hw-') === -1) node.removeAttribute('class');
      var tag = node.tagName;
      if (node.hasAttribute('style') && tag !== 'IMG' && tag !== 'IFRAME' && tag !== 'VIDEO' && tag !== 'TD' && tag !== 'TH' && tag !== 'COL') {
        node.removeAttribute('style');
      }
      if (tag === 'FONT') { node.removeAttribute('color'); node.removeAttribute('face'); node.removeAttribute('size'); }
    });
  }

  /* ── 2. flatten nested wrappers into a list of block elements ────────────── */
  function flatten(body) {
    var out = [];
    var para = null;

    function flush() {
      if (para) {
        trimBreaks(para);
        if (txt(para) || para.querySelector('img,iframe,video')) out.push(para);
        para = null;
      }
    }
    function addInline(node) {
      if (!para) para = document.createElement('p');
      para.appendChild(node);
    }
    function isBreak(node) { return node && node.nodeType === 1 && node.tagName === 'BR'; }
    function nextMeaningful(kids, i) {
      for (var j = i + 1; j < kids.length; j++) {
        if (kids[j].nodeType === 3 && !kids[j].nodeValue.trim()) continue;
        return { node: kids[j], index: j };
      }
      return null;
    }

    function walk(node) {
      var kids = Array.prototype.slice.call(node.childNodes);
      for (var i = 0; i < kids.length; i++) {
        var k = kids[i];

        if (k.nodeType === 3) {
          var t = k.nodeValue;
          if (!t.trim()) {
            if (para) { if (/\n\s*\n/.test(t)) flush(); else addInline(document.createTextNode(' ')); }
            continue;
          }
          // a blank line in the source is the author's paragraph break
          var parts = t.split(/\n[ \t]*\n/);
          for (var p = 0; p < parts.length; p++) {
            if (p > 0) flush();
            if (parts[p].trim()) addInline(document.createTextNode(parts[p]));
          }
          continue;
        }
        if (k.nodeType !== 1) continue;

        var tag = k.tagName;
        if (tag === 'BR') {
          var nx = nextMeaningful(kids, i);
          if (nx && isBreak(nx.node)) {
            flush();
            while (nx && isBreak(nx.node)) { i = nx.index; nx = nextMeaningful(kids, i); }
          } else if (para) {
            addInline(k);
          }
          continue;
        }
        if (tag === 'P') {
          flush();
          splitParagraph(k).forEach(function (p2) { out.push(p2); });
          continue;
        }
        if (LEAF[tag]) { flush(); out.push(k); continue; }
        if (tag === 'IMG') {
          if (!para || !txt(para)) { flush(); out.push(k); } else addInline(k);
          continue;
        }
        if (CONTAINER[tag] || k.querySelector(BLOCKISH)) { flush(); walk(k); flush(); continue; }
        addInline(k);
      }
    }

    walk(body);
    flush();
    return out;
  }

  function trimBreaks(p) {
    while (p.firstChild && ((p.firstChild.nodeType === 1 && p.firstChild.tagName === 'BR') || (p.firstChild.nodeType === 3 && !p.firstChild.nodeValue.trim()))) p.removeChild(p.firstChild);
    while (p.lastChild && ((p.lastChild.nodeType === 1 && p.lastChild.tagName === 'BR') || (p.lastChild.nodeType === 3 && !p.lastChild.nodeValue.trim()))) p.removeChild(p.lastChild);
  }

  // <p>one<br><br>two</p>  ->  <p>one</p><p>two</p>
  function splitParagraph(p) {
    var result = [];
    var cur = document.createElement('p');
    var kids = Array.prototype.slice.call(p.childNodes);
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.nodeType === 1 && k.tagName === 'BR') {
        var j = i + 1;
        while (j < kids.length && kids[j].nodeType === 3 && !kids[j].nodeValue.trim()) j++;
        if (j < kids.length && kids[j].nodeType === 1 && kids[j].tagName === 'BR') {
          result.push(cur);
          cur = document.createElement('p');
          while (j < kids.length && ((kids[j].nodeType === 1 && kids[j].tagName === 'BR') || (kids[j].nodeType === 3 && !kids[j].nodeValue.trim()))) j++;
          i = j - 1;
          continue;
        }
      }
      cur.appendChild(k);
    }
    result.push(cur);
    return result.filter(function (el) {
      trimBreaks(el);
      return txt(el) || el.querySelector('img,iframe,video');
    });
  }

  /* ── 3. classify ─────────────────────────────────────────────────────────── */
  function onlyEmphasis(p) {
    // every bit of visible text sits inside <strong>/<b>/<u> (optionally with <em>)
    var ok = true;
    var found = false;
    (function check(node, inside) {
      Array.prototype.forEach.call(node.childNodes, function (k) {
        if (!ok) return;
        if (k.nodeType === 3) {
          if (k.nodeValue.replace(/[\u00a0\s:]+/g, '') && !inside) ok = false;
          else if (k.nodeValue.trim()) found = true;
        } else if (k.nodeType === 1) {
          var t = k.tagName;
          if (t === 'BR') return;
          if (t === 'A' || t === 'IMG' || t === 'IFRAME') { ok = false; return; }
          check(k, inside || t === 'STRONG' || t === 'B' || t === 'U');
        }
      });
    })(p, false);
    return ok && found;
  }

  function firstMeaningfulChild(p) {
    for (var i = 0; i < p.childNodes.length; i++) {
      var k = p.childNodes[i];
      if (k.nodeType === 3 && !k.nodeValue.trim()) continue;
      return k;
    }
    return null;
  }

  // body of a definition = the paragraph minus its leading term
  function defBody(item) {
    var p = item.el;
    var out = document.createElement('p');
    out.className = 'hw-story__card-body';
    if (item.termNode) {
      p.removeChild(item.termNode);
    } else if (item.textNode) {
      item.textNode.nodeValue = item.rest;
    }
    while (p.firstChild) out.appendChild(p.firstChild);
    var first = out.firstChild;
    if (first && first.nodeType === 3) first.nodeValue = first.nodeValue.replace(/^[\s:\u2014\u2013\-]+/, '');
    trimBreaks(out);
    first = out.firstChild;
    if (first && first.nodeType === 3) first.nodeValue = first.nodeValue.replace(/^[\s:\u2014\u2013\-]+/, '');

    // "...text<br><a>Install instructions</a>" — a link on its own last line
    // becomes the card's resource button
    item.tail = [];
    var last = out.lastChild;
    while (last && last.nodeType === 3 && !last.nodeValue.trim()) { out.removeChild(last); last = out.lastChild; }
    if (last && last.nodeType === 1 && last.tagName === 'A' && last.getAttribute('href')) {
      var prev = last.previousSibling;
      while (prev && prev.nodeType === 3 && !prev.nodeValue.trim()) prev = prev.previousSibling;
      if (prev && prev.nodeType === 1 && prev.tagName === 'BR') {
        out.removeChild(last);
        item.tail.push(last);
        trimBreaks(out);
      }
    }
    return out;
  }

  // "Cummins 10mm High Speed CP3" is a name; "All pumps are brand new" is a sentence.
  // Only names become card titles, so ordinary sentences with a dash stay prose.
  function looksLikeName(term) {
    var words = term.trim().split(/\s+/);
    if (words.length > 10) return false;
    var caps = words.filter(function (w) { return /^[A-Z0-9(&#"\u201c]/.test(w) || /^(w\/|of|and|for|to|the|with|or|in|on)$/i.test(w); }).length;
    var strong = words.filter(function (w) { return /^[A-Z0-9(&#"\u201c]/.test(w); }).length;
    return strong >= 1 && caps / words.length >= 0.75 && strong / words.length >= 0.5;
  }

  // "The CAN Keyboard allows:" / "Larger flow rates may require:" introduce the
  // list that follows — they are sentences, not section titles.
  function isLeadIn(t) {
    var s = t.replace(/\s*:\s*$/, '');
    if (/^(the|it|this|these|those|our|we|you|your|a|an|if|when|for|with|all|each|every)\b/i.test(s)) return true;
    if (/\b(to|for|with|by|of|and|or|is|are|can|will|may|must|should|allows?|requires?|offers?|provides?|includes?|features?)$/i.test(s) && s.split(/\s+/).length > 2) return true;
    return false;
  }

  // decorative emoji / symbols at the start of a pasted heading
  function cleanTitle(t) {
    return t.replace(/^[^A-Za-z0-9"'(\u201c$#]+/, '').trim() || t;
  }
  function isWarningTitle(raw) {
    return /^\s*(\u26a0|\u2716|\u274c|\u26d4|\ud83d\udeab)/.test(raw) || /\b(warning|important|notice|disclaimer|not for sale)\b/i.test(raw);
  }

  // <p>• one<br>• two</p>  ->  real list
  function bulletLines(p) {
    var lines = [];
    var cur = document.createElement('li');
    Array.prototype.slice.call(p.childNodes).forEach(function (k) {
      if (k.nodeType === 1 && k.tagName === 'BR') { lines.push(cur); cur = document.createElement('li'); }
      else cur.appendChild(k.cloneNode(true));
    });
    lines.push(cur);
    lines = lines.filter(function (li) { return txt(li); });
    if (lines.length < 2) return null;
    var BULLET = /^\s*[\u2022\u2023\u25aa\u25cf\u2713\u2714\u2705\u27a4\u25b8\-\*]\s+/;
    for (var i = 0; i < lines.length; i++) { if (!BULLET.test(lines[i].textContent)) return null; }
    var ul = document.createElement('ul');
    lines.forEach(function (li) {
      // remove the typed bullet from the first text node
      var walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT, null);
      var n;
      while ((n = walker.nextNode())) {
        if (n.nodeValue.trim()) { n.nodeValue = n.nodeValue.replace(BULLET, ''); break; }
      }
      ul.appendChild(li);
    });
    return ul;
  }

  function classify(node) {
    var tag = node.tagName;
    if (/^H[1-6]$/.test(tag)) {
      var hLinks = node.querySelectorAll('a[href]');
      if (hLinks.length) {
        var hLinkText = Array.prototype.map.call(hLinks, function (a) { return txt(a); }).join('');
        // a heading that is nothing but a link is a resource (manual, drawing…)
        if (hLinkText.replace(/[\s|,\u00b7]+/g, '') === txt(node).replace(/[\s|,\u00b7]+/g, '')) {
          return { type: 'links', links: Array.prototype.slice.call(hLinks) };
        }
        // otherwise keep the title and carry its links along as buttons
        return { type: 'h', text: cleanTitle(stripEnd(txt(node))), warn: isWarningTitle(txt(node)), extraLinks: Array.prototype.slice.call(hLinks) };
      }
      return { type: 'h', text: cleanTitle(stripEnd(txt(node))), warn: isWarningTitle(txt(node)) };
    }
    if (tag === 'UL' || tag === 'OL') return { type: 'list', el: node };
    if (tag === 'TABLE') return { type: 'table', el: node };
    if (tag === 'BLOCKQUOTE') return { type: 'quote', el: node };
    if (tag === 'HR') return { type: 'rule' };
    if (tag === 'IFRAME' || tag === 'VIDEO') return { type: 'video', el: node };
    if (tag === 'IMG' || tag === 'FIGURE') return { type: 'img', el: node };
    if (tag === 'DL' || tag === 'PRE') return { type: 'raw', el: node };

    // paragraphs
    var t = txt(node);
    var media = node.querySelectorAll('iframe,video,img');
    if (!t && media.length) {
      var first = media[0];
      // keep a linked picture linked
      var linked = first.tagName === 'IMG' && first.closest ? first.closest('a') : null;
      if (linked && !node.contains(linked)) linked = null;
      return (first.tagName === 'IMG') ? { type: 'img', el: linked || first } : { type: 'video', el: first };
    }
    if (!t) return null;

    // link-only line → resource button(s)
    var links = node.querySelectorAll('a[href]');
    if (links.length) {
      var linkText = Array.prototype.map.call(links, function (a) { return txt(a); }).join('');
      if (linkText.replace(/[\s|,\u00b7]+/g, '') === t.replace(/[\s|,\u00b7]+/g, '')) {
        return { type: 'links', links: Array.prototype.slice.call(links) };
      }
    }

    // typed bullets on separate lines
    var bullets = bulletLines(node);
    if (bullets) return { type: 'list', el: bullets };

    // heading-like
    if (t.length <= 90 && onlyEmphasis(node) && !(/:$/.test(t) && isLeadIn(t))) return { type: 'h', text: cleanTitle(stripEnd(t)), warn: isWarningTitle(t) };
    if (t.length <= 64 && /:$/.test(t) && !/[.!?]\s/.test(t) && !links.length && !isLeadIn(t)) return { type: 'h', text: cleanTitle(stripEnd(t)), warn: isWarningTitle(t) };

    if (NOTE_RE.test(t)) return { type: 'note', el: node };

    // definition: "<strong>Term:</strong> body"  (nothing is moved here — the
    // paragraph is only taken apart at render time, and only when it sits in a
    // run of two or more definitions)
    var fc = firstMeaningfulChild(node);
    if (fc && fc.nodeType === 1 && (fc.tagName === 'STRONG' || fc.tagName === 'B') && !fc.querySelector('img,iframe,video,a')) {
      var term = stripEnd(txt(fc));
      var restLen = t.length - txt(fc).length;
      if (term.length >= 2 && term.length <= 110 && restLen >= 12) {
        return { type: 'def', term: term, el: node, termNode: fc };
      }
    }
    // definition: "Term — body" in plain text
    if (fc && fc.nodeType === 3) {
      var m = fc.nodeValue.match(/^\s*([^\u2014\u2013.!?:]{3,72}?)\s+[\u2014\u2013]\s+(\S[\s\S]*)$/);
      if (m && looksLikeName(m[1]) && (t.length - m[1].length) >= 20) {
        return { type: 'def', term: m[1].trim(), el: node, textNode: fc, rest: m[2] };
      }
    }
    return { type: 'p', el: node };
  }

  /* ── 4. render ───────────────────────────────────────────────────────────── */
  function linkButton(a) {
    var href = a.getAttribute('href') || '#';
    var btn = mk('a', 'hw-story__link');
    btn.setAttribute('href', href);
    var icon = ICON.ext;
    if (/^mailto:/i.test(href)) icon = ICON.mail;
    else if (/\.pdf(\?|#|$)/i.test(href) || /instruction|install|manual|guide|sheet/i.test(txt(a))) icon = ICON.doc;
    if (!/^(mailto:|tel:|#|\/)/i.test(href)) {
      btn.setAttribute('target', '_blank');
      btn.setAttribute('rel', 'noopener noreferrer');
    }
    btn.innerHTML = icon;
    var label = mk('span');
    label.textContent = txt(a);
    btn.appendChild(label);
    return btn;
  }

  function renderItems(items, block) {
    // a "definition" only counts when it is part of a run of two or more;
    // on its own it is an ordinary paragraph and is left exactly as written
    for (var d = 0; d < items.length; d++) {
      if (items[d].type !== 'def') continue;
      var end = d, count = 0;
      while (end < items.length && (items[end].type === 'def' || (items[end].type === 'links' && count))) {
        if (items[end].type === 'def') count++;
        end++;
      }
      if (count < 2) items[d].type = 'p';
      d = end - 1;
    }

    var i = 0;
    while (i < items.length) {
      var it = items[i];

      if (it.type === 'def') {
        var run = [];
        while (i < items.length && (items[i].type === 'def' || (items[i].type === 'links' && run.length))) {
          if (items[i].type === 'def') run.push({ def: items[i], links: [] });
          else run[run.length - 1].links = run[run.length - 1].links.concat(items[i].links);
          i++;
        }
        var grid = mk('div', 'hw-story__cards');
        var total = 0;
        run.forEach(function (entry, n) {
          var card = mk('article', 'hw-story__card');
          var num = mk('span', 'hw-story__card-n'); num.textContent = pad(n + 1);
          var title = mk('h4', 'hw-story__card-title'); title.textContent = entry.def.term;
          var bodyP = defBody(entry.def);
          card.appendChild(num); card.appendChild(title); card.appendChild(bodyP);
          var cardLinks = (entry.def.tail || []).concat(entry.links);
          if (cardLinks.length) {
            var row = mk('div', 'hw-story__card-links');
            cardLinks.forEach(function (a) { row.appendChild(linkButton(a)); });
            card.appendChild(row);
          }
          total += txt(bodyP).length;
          grid.appendChild(card);
        });
        // longer write-ups get wider cards
        if (total / run.length > 230) grid.classList.add('hw-story__cards--wide');
        block.appendChild(grid);
        continue;
      }

      if (it.type === 'p' || it.type === 'raw') {
        var prose = mk('div', 'hw-story__prose');
        while (i < items.length && (items[i].type === 'p' || items[i].type === 'raw')) { prose.appendChild(items[i].el); i++; }
        block.appendChild(prose);
        continue;
      }

      if (it.type === 'links') {
        var rowL = mk('div', 'hw-story__links');
        while (i < items.length && items[i].type === 'links') {
          items[i].links.forEach(function (a) { rowL.appendChild(linkButton(a)); });
          i++;
        }
        block.appendChild(rowL);
        continue;
      }

      if (it.type === 'list') {
        var panel = mk('div', 'hw-story__panel');
        var lis = it.el.children;
        var shortItems = lis.length >= 6 && Array.prototype.every.call(lis, function (li) { return txt(li).length <= 56; });
        // "Label: value" lists read as a spec sheet
        var labelled = Array.prototype.filter.call(lis, function (li) {
          var f = firstMeaningfulChild(li);
          return f && f.nodeType === 1 && (f.tagName === 'STRONG' || f.tagName === 'B') && txt(li).length > txt(f).length + 1;
        }).length;
        var isSpec = it.el.tagName !== 'OL' && lis.length >= 2 && labelled / lis.length >= 0.7;
        if (isSpec) {
          Array.prototype.forEach.call(lis, function (li) {
            var f = firstMeaningfulChild(li);
            if (!f || f.nodeType !== 1 || (f.tagName !== 'STRONG' && f.tagName !== 'B')) return;
            var val = document.createElement('span');
            var k = f.nextSibling;
            while (k) { var nxt = k.nextSibling; val.appendChild(k); k = nxt; }
            if (val.firstChild && val.firstChild.nodeType === 3) val.firstChild.nodeValue = val.firstChild.nodeValue.replace(/^[\s:\u2014\u2013\-]+/, '');
            f.textContent = stripEnd(txt(f));
            li.appendChild(val);
          });
          shortItems = false;
        }
        it.el.className = 'hw-story__list' + (it.el.tagName === 'OL' ? ' hw-story__list--num' : '') + (shortItems ? ' hw-story__list--cols' : '') + (isSpec ? ' hw-story__list--spec' : '');
        panel.appendChild(it.el);
        block.appendChild(panel);
        i++;
        continue;
      }

      if (it.type === 'note') {
        var note = mk('div', 'hw-story__note', ICON.note);
        var wrap = mk('div');
        wrap.appendChild(it.el);
        note.appendChild(wrap);
        block.appendChild(note);
        i++;
        continue;
      }

      if (it.type === 'video') {
        var fig = mk('div', 'hw-story__media hw-story__media--video');
        it.el.removeAttribute('width'); it.el.removeAttribute('height'); it.el.removeAttribute('style');
        if (it.el.tagName === 'IFRAME') {
          it.el.setAttribute('loading', 'lazy');
          it.el.setAttribute('allowfullscreen', '');
          if (!it.el.getAttribute('title')) it.el.setAttribute('title', 'Product video');
        }
        fig.appendChild(it.el);
        block.appendChild(fig);
        i++;
        continue;
      }

      if (it.type === 'img') {
        var imgs = [];
        while (i < items.length && items[i].type === 'img') { imgs.push(items[i].el); i++; }
        var holder = imgs.length > 1 ? mk('div', 'hw-story__gallery') : block;
        imgs.forEach(function (im) {
          var f = mk('figure', 'hw-story__media');
          var pic = im.tagName === 'IMG' ? im : im.querySelector('img');
          if (pic) { pic.removeAttribute('style'); pic.setAttribute('loading', 'lazy'); }
          f.appendChild(im);
          holder.appendChild(f);
        });
        if (holder !== block) block.appendChild(holder);
        continue;
      }

      if (it.type === 'table') {
        var tw = mk('div', 'hw-story__table');
        tw.appendChild(it.el);
        block.appendChild(tw);
        i++;
        continue;
      }

      if (it.type === 'quote') {
        it.el.className = 'hw-story__quote';
        block.appendChild(it.el);
        i++;
        continue;
      }

      i++;
    }
  }

  /* ── 5. build ────────────────────────────────────────────────────────────── */
  function build(root) {
    var body = root.querySelector('[data-hw-story-body]');
    var leadEl = root.querySelector('[data-hw-story-lead]');
    var grid = root.querySelector('.hw-story__grid');
    if (!body || body.getAttribute('data-hw-built')) return;
    body.setAttribute('data-hw-built', '1');

    // hand-built HTML in a description is left alone
    if (body.querySelector('script,style,form,[class*="hw-"]')) return;

    var original = body.cloneNode(true);
    try {
      clean(body);
      var blocks = flatten(body);
      var items = [];
      blocks.forEach(function (b) {
        var c = classify(b);
        if (!c) return;
        // A heading only keeps its words. Pictures or videos that were pasted
        // inside one are lifted out and shown just above it — never dropped.
        if (c.type === 'h' && b.querySelectorAll) {
          Array.prototype.forEach.call(b.querySelectorAll('img,iframe,video'), function (m) {
            items.push(m.tagName === 'IMG' ? { type: 'img', el: m } : { type: 'video', el: m });
          });
        }
        items.push(c);
        if (c.extraLinks) items.push({ type: 'links', links: c.extraLinks });
      });

      // a description that opens with "<strong>Product headline</strong> text…"
      // is a headline + intro, not the first card of a grid
      if (items.length && items[0].type === 'def' && items[0].termNode) {
        var open = items[0];
        open.el.removeChild(open.termNode);
        var leftover = splitParagraph(open.el).map(function (pp) {
          var fn = pp.firstChild;
          if (fn && fn.nodeType === 3) fn.nodeValue = fn.nodeValue.replace(/^[\s:\u2014\u2013\-]+/, '');
          return classify(pp) || { type: 'p', el: pp };
        });
        items = [{ type: 'h', text: cleanTitle(open.term) }].concat(leftover, items.slice(1));
      }

      // sections split at headings and rules
      var sections = [{ title: null, items: [] }];
      items.forEach(function (it) {
        if (it.type === 'h') sections.push({ title: it.text, warn: it.warn, items: [] });
        else if (it.type === 'rule') { if (sections[sections.length - 1].items.length) sections.push({ title: null, items: [] }); }
        else sections[sections.length - 1].items.push(it);
      });
      // a heading with nothing under it becomes a line of bold text (joined to
      // the section above) rather than vanishing or floating on its own
      for (var si = 0; si < sections.length; si++) {
        var sx = sections[si];
        if (!sx.title || sx.items.length) continue;
        var bp = document.createElement('p');
        var bs = document.createElement('strong');
        bs.textContent = sx.title;
        bp.appendChild(bs);
        if (si > 0) {
          sections[si - 1].items.push({ type: 'p', el: bp });
          sections.splice(si, 1);
          si--;
        } else {
          sx.items.push({ type: 'p', el: bp });
          sx.title = null;
        }
      }
      sections = sections.filter(function (s) { return s.items.length; });

      // lead = first plain paragraph of the opening (untitled) section
      if (leadEl && sections.length && !sections[0].title) {
        var firstSec = sections[0];
        for (var li = 0; li < firstSec.items.length; li++) {
          var cand = firstSec.items[li];
          if (cand.type === 'video' || cand.type === 'img') continue;
          if (cand.type === 'p' && txt(cand.el).length >= 60) {
            leadEl.appendChild(cand.el);
            firstSec.items.splice(li, 1);
          }
          break;
        }
        if (!firstSec.items.length) sections.shift();
      }

      var frag = document.createDocumentFragment();
      sections.forEach(function (sec, idx) {
        var block = mk('section', 'hw-story__block');
        if (sec.title) {
          var head = mk('div', 'hw-story__block-head');
          var h = mk('h3', 'hw-story__h');
          h.textContent = sec.title;
          head.appendChild(h);
          block.appendChild(head);
        } else if (idx === 0) {
          var kick = mk('span', 'hw-story__kicker');
          kick.textContent = 'Overview';
          var headK = mk('div', 'hw-story__block-head');
          headK.style.marginBottom = '12px';
          headK.appendChild(kick);
          block.appendChild(headK);
        }
        if (sec.warn && sec.items.every(function (x) { return x.type === 'p' || x.type === 'note' || x.type === 'links'; })) {
          // "Important purchase notice" style sections read as one callout
          var callout = mk('div', 'hw-story__note', ICON.note);
          var inner = mk('div');
          sec.items.forEach(function (x) {
            if (x.type === 'links') {
              var lr = mk('div', 'hw-story__links');
              x.links.forEach(function (a) { lr.appendChild(linkButton(a)); });
              inner.appendChild(lr);
            } else inner.appendChild(x.el);
          });
          callout.appendChild(inner);
          block.appendChild(callout);
        } else {
          renderItems(sec.items, block);
        }
        frag.appendChild(block);
      });

      while (body.firstChild) body.removeChild(body.firstChild);
      body.classList.remove('hw-story__body--raw');
      body.appendChild(frag);

      if (!sections.length) {
        body.hidden = true;
        if (grid) grid.classList.add('hw-story__grid--brief');
      }
    } catch (err) {
      // never leave the customer with a broken description
      body.parentNode.replaceChild(original, body);
      original.setAttribute('data-hw-built', 'fallback');
      if (leadEl) leadEl.innerHTML = '';
      if (window.console) console.warn('[hw] product story fell back to the plain description', err);
    }
  }

  function init() {
    var root = document.getElementById('hw-story');
    if (!root) return;
    build(root);

    // keep the Part # row in step with the selected option
    var sku = root.querySelector('[data-hw-story-sku]');
    if (sku) {
      document.addEventListener('variant:change', function (e) {
        var v = e.detail && e.detail.variant;
        if (!v) return;
        var row = sku.closest('.hw-story__spec');
        if (v.sku) { sku.textContent = v.sku; if (row) row.hidden = false; }
        else if (row) row.hidden = true;
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // exposed for the theme editor and for testing
  window.HWProductStory = { build: build };
})();
